import type {
  AlertButton,
  AlertButtonResponse,
  AlertButtonVariant,
  AlertType,
} from '../store/alert-store';

import { useAlertStore } from '../store/alert-store';

// Re-export for consumer convenience
export type { AlertButton, AlertButtonResponse, AlertButtonVariant };

/**
 * Default button presets used by `createWithButtons` when no buttons are supplied.
 */
export const DEFAULT_ALERT_BUTTONS: AlertButton[] = [
  { value: 'cancel', label: 'Cancel', variant: 'secondary' },
  { value: 'ignore', label: 'Ignore', variant: 'ghost' },
  { value: 'continue', label: 'Continue', variant: 'primary' },
];

/**
 * Composable for using the alert system with the Composition API
 */
export function useAlert() {
  const alertStore = useAlertStore();

  return {
    /**
     * Show a success alert
     * @param message The message to display
     * @param keepAlive If true, the alert will not auto-dismiss
     * @param duration Duration in milliseconds before the alert disappears (if not keepAlive)
     * @returns The ID of the created alert
     */
    success(message: string, keepAlive = false, duration = 10000) {
      return alertStore.showAlert('success', message, keepAlive, duration);
    },

    /**
     * Show an info alert
     * @param message The message to display
     * @param keepAlive If true, the alert will not auto-dismiss
     * @param duration Duration in milliseconds before the alert disappears (if not keepAlive)
     * @returns The ID of the created alert
     */
    info(message: string, keepAlive = false, duration = 10000) {
      return alertStore.showAlert('info', message, keepAlive, duration);
    },

    /**
     * Show a warning alert
     * @param message The message to display
     * @param keepAlive If true, the alert will not auto-dismiss
     * @param duration Duration in milliseconds before the alert disappears (if not keepAlive)
     * @returns The ID of the created alert
     */
    warning(message: string, keepAlive = false, duration = 10000) {
      return alertStore.showAlert('warning', message, keepAlive, duration);
    },

    /**
     * Show an error alert
     * @param message The message to display
     * @param keepAlive If true, the alert will not auto-dismiss
     * @param duration Duration in milliseconds before the alert disappears (if not keepAlive)
     * @returns The ID of the created alert
     */
    error(message: string, keepAlive = false, duration = 10000) {
      return alertStore.showAlert('error', message, keepAlive, duration);
    },

    /**
     * Show an alert with the specified type
     * @param type The type of alert: 'success', 'info', 'warning', or 'error'
     * @param message The message to display
     * @param keepAlive If true, the alert will not auto-dismiss
     * @param duration Duration in milliseconds before the alert disappears (if not keepAlive)
     * @returns The ID of the created alert
     */
    show(
      type: AlertType,
      message: string,
      keepAlive = false,
      duration = 10000,
    ) {
      return alertStore.showAlert(type, message, keepAlive, duration);
    },

    /**
     * Hide a specific alert by ID
     * @param id The ID of the alert to hide
     */
    hide(id: string | null) {
      if (!id) return; // If no ID is provided, do nothing
      alertStore.hideAlert(id);
    },

    /**
     * Hide all currently displayed alerts
     */
    hideAll() {
      alertStore.hideAllAlerts();
    },

    /**
     * Show or update a progress alert
     * @param message The message to display
     * @param progressPercent The progress percentage (0-100)
     * @param id Optional ID for updating existing progress alert
     * @returns The ID of the created/updated alert
     */
    progress(message: string, progressPercent: number, id?: string) {
      return alertStore.progress(message, progressPercent, id);
    },

    /**
     * Update an existing alert with new content
     * @param id The ID of the alert to update
     * @param message The new message to display
     * @returns The same alert ID if successful, or a new alert ID if the original was not found
     */
    update(id: string | null, message: string) {
      alertStore.updateAlert(id, message); // Hide the alert first
      return id;
    },

    /**
     * Show a persistent alert with interactive buttons.
     * Returns a Promise that resolves with the user's choice.
     *
     * If no `buttons` array is provided the default set is used:
     * **Cancel** (secondary) · **Ignore** (ghost) · **Continue** (primary)
     *
     * @param type     The visual type of the alert
     * @param message  The message to display
     * @param buttons  Custom button definitions (optional, uses DEFAULT_ALERT_BUTTONS)
     * @returns Promise resolving to `{ action, alertId }`
     *
     * @example
     * ```ts
     * const alert = useAlert();
     * const { action } = await alert.createWithButtons(
     *   'warning',
     *   'You have unsaved changes.',
     * );
     * if (action === 'continue') { doSomething(); }
     * ```
     */
    async createWithButtons(
      type: AlertType,
      message: string,
      buttons: AlertButton[] = DEFAULT_ALERT_BUTTONS,
    ): Promise<AlertButtonResponse> {
      return alertStore.showAlertWithButtons(type, message, buttons);
    },

    /**
     * Quick confirmation dialog.
     * Displays a warning alert with **Cancel** and a customisable confirm label.
     *
     * @param message       The confirmation message
     * @param confirmLabel  Label for the confirm button (default: "Confirm")
     * @param type          Alert type (default: 'warning')
     * @returns `true` if the user confirmed, `false` if cancelled
     *
     * @example
     * ```ts
     * const alert = useAlert();
     * if (await alert.confirm('Delete this item?')) {
     *   deleteItem();
     * }
     * ```
     */
    async confirm(
      message: string,
      confirmLabel = 'Confirm',
      type: AlertType = 'warning',
    ): Promise<boolean> {
      const { action } = await alertStore.showAlertWithButtons(type, message, [
        { value: 'cancel', label: 'Cancel', variant: 'secondary' },
        { value: 'confirm', label: confirmLabel, variant: 'primary' },
      ]);
      return action === 'confirm';
    },
  };
}
